/**
 * Script Delay Loader
 *
 * Delays loading of non-critical JavaScript until user interaction or timeout.
 * This improves initial page load performance by deferring third-party scripts.
 *
 * HTML Attributes:
 * - delay="url"           : URL of script to load after user interaction or timeout
 * - await="url"           : Alternative to delay, used with ID-based loading
 * - delayed-time="ms"     : Additional milliseconds to wait before loading
 * - run-after-load="ids"  : Comma-separated script IDs to load after this script
 *
 * Usage Examples:
 *
 * Basic:
 *   <script delay="https://example.com/analytics.js"></script>
 *
 * With extra delay:
 *   <script delay="https://example.com/script.js" delayed-time="3000"></script>
 *
 * Chained loading:
 *   <script id="main" delay="https://example.com/main.js"></script>
 *   <script id="dependent" await="https://example.com/lib.js" run-after-load="main"></script>
 */
(function (window) {
  // How long to wait before auto-loading scripts if no user interaction occurs (in seconds)
  const autoLoadDuration = 5;

  // User interaction events that trigger immediate script loading
  const eventList = ["keydown", "mousemove", "wheel", "touchmove", "touchstart", "touchend"];

  // Fallback timer to load scripts after 5 seconds even without interaction
  const autoLoadTimeout = setTimeout(runScripts, autoLoadDuration * 1000);

  // Track loaded scripts to prevent duplicates
  const scriptsLoaded = []

  // Set up listeners for user interaction events
  eventList.forEach(function (event) {
    window.addEventListener(event, triggerScripts, { passive: true })
  });

  /**
   * Triggers script loading and removes event listeners
   * Called on first user interaction
   */
  function triggerScripts() {
    runScripts();
    clearTimeout(autoLoadTimeout);
    eventList.forEach(function (event) {
      window.removeEventListener(event, triggerScripts, { passive: true });
    });
  }

  /**
   * Loads a script asynchronously and prevents duplicate loading
   * @param {string} src - The URL of the script to load
   * @param {Function} callback - Optional callback to run after script loads
   */
  const async = (src, callback) => {
    // Skip if this script was already loaded
    if (scriptsLoaded.includes(src)) return

    const createdElement = document.createElement('script');

    createdElement.src = src;
    createdElement.type = 'module'; // Enable ES6 imports in delayed scripts

    document.body.appendChild(createdElement);

    if (callback) {
      createdElement.addEventListener('load', (e) => {
        callback(null);
        scriptsLoaded.push(src)
      }, false);
    }
  }

  /**
   * Gets a script tag by ID and initiates its loading
   * Used for chained script loading via run-after-load attribute
   * @param {string} id - The ID of the script tag to load
   */
  function getAwaitScriptTag(id) {
    const tag = document.getElementById(id)
    if (!tag) return
    const src = tag.getAttribute("await")
    getDelayTime(tag, src)
  }

  /**
   * Applies optional delay before loading a script
   * @param {HTMLElement} tag - The script tag element
   * @param {string} src - The URL of the script to load
   */
  function getDelayTime(tag, src) {
    const delayedTimeAttribute = tag.getAttribute("delayed-time") || 0
    const delayedTime = parseInt(delayedTimeAttribute, 10)
    setTimeout(() => {
      async(src, () => getRunAfterLoadAttribute(tag))
    }, delayedTime)
  }

  /**
   * Loads any scripts that should run after this script completes
   * Enables sequential loading of dependent scripts
   * @param {HTMLElement} scriptTag - The script tag with run-after-load attribute
   */
  function getRunAfterLoadAttribute(scriptTag) {
    const newScriptId = scriptTag.getAttribute("run-after-load")
    if (!newScriptId) return

    const scriptsToLoad = newScriptId.split(',')
    scriptsToLoad.forEach(singleScript => {
      getAwaitScriptTag(singleScript)
    });
  }

  /**
   * Finds and loads all scripts with the delay attribute
   * This is the main entry point called on user interaction or timeout
   */
  function runScripts() {
    document.querySelectorAll("script[delay]").forEach(function (scriptTag) {
      const src = scriptTag.getAttribute("delay")
      getDelayTime(scriptTag, src)
    })
  }
})(this);
